<script setup>
import { computed } from 'vue';

/**
 * Grouped list container with an optional colored surface.
 *
 * @status recommended
 * @group lists-tables
 * @useWhen Visually grouping related HrListItem rows under an optionally colored section surface
 * @avoidWhen A flat unstyled list — wrap items directly without a group container
 * @related HrListItem
 * @related HrListSectionHeader
 * @within hr-list
 * @a11y Renders as role="group". Wire aria-labelledby on this element to the id of the HrListSectionHeader inside.
 */
defineOptions({ name: 'HrListGroup' });

const props = defineProps({
  /**
   * Colored surface variant — maps to the corresponding M3 *-container token.
   * Omit for a transparent (unstyled) grouping.
   * @values warning, error, success, primary, secondary, tertiary
   */
  variant: {
    type: String,
    default: null,
  },
});

const containerTokens = {
  warning: 'var(--app-warning-container)',
  error: 'var(--app-error-container)',
  success: 'var(--app-success-container)',
  primary: 'var(--app-primary-container)',
  secondary: 'var(--app-secondary-container)',
  tertiary: 'var(--app-tertiary-container)',
};

const onContainerTokens = {
  warning: 'var(--app-on-warning-container)',
  error: 'var(--app-on-error-container)',
  success: 'var(--app-on-success-container)',
  primary: 'var(--app-on-primary-container)',
  secondary: 'var(--app-on-secondary-container)',
  tertiary: 'var(--app-on-tertiary-container)',
};

const containerStyle = computed(() => {
  if (!props.variant) return undefined;
  return {
    background: containerTokens[props.variant],
    '--hr-on-container': onContainerTokens[props.variant],
  };
});
</script>

<template>
  <!--
    Semantically a labelled group region, not a Card. Each row inside is an
    independent action — the wrapper is only a named grouping surface.
    Consumers should wire aria-labelledby to the id of the section header inside.
  -->
  <div
    role="group"
    class="hr-list-group"
    :style="containerStyle"
  >
    <!-- @slot List items and section headers. -->
    <slot />
  </div>
</template>

<style lang="scss" scoped>

.hr-list-group {
  border-radius: var(--app-shape-corner-medium);
  overflow: hidden;
  margin: 8px;
}

</style>
